// src/features/statistics/kpi-cards.tsx
import { formatCount } from "@/lib/format-count";
import type { StatisticsKpis } from "@/lib/types";
import {
  Boxes,
  Hash,
  type LucideIcon,
  MessagesSquare,
  Network,
  UserCheck,
  UserMinus,
  Users,
} from "lucide-react";

interface KpiCardsProps {
  kpis: StatisticsKpis;
  isRefreshing?: boolean;
}

type Tone = "magenta" | "purple" | "cyan" | "orange";

/**
 * Icon-chip tone colors, mirroring `.iris-kpi-tile__icon[data-tone]` in
 * iris-kit.css. Kept as rgba literals here because the translucent chip
 * background needs an alpha channel the solid `--iris-neon-*` tokens don't carry.
 */
const TONE: Record<Tone, { fg: string; bg: string; border: string }> = {
  magenta: {
    fg: "var(--iris-neon-magenta)",
    bg: "rgba(255, 46, 166, 0.1)",
    border: "rgba(255, 46, 166, 0.25)",
  },
  purple: {
    fg: "var(--iris-neon-purple)",
    bg: "rgba(168, 85, 247, 0.1)",
    border: "rgba(168, 85, 247, 0.25)",
  },
  cyan: {
    fg: "var(--iris-neon-cyan)",
    bg: "rgba(34, 211, 255, 0.1)",
    border: "rgba(34, 211, 255, 0.25)",
  },
  orange: {
    fg: "var(--iris-neon-orange)",
    bg: "rgba(255, 153, 51, 0.1)",
    border: "rgba(255, 153, 51, 0.25)",
  },
};

const CARDS: {
  key: keyof StatisticsKpis;
  label: string;
  icon: LucideIcon;
  tone: Tone;
  format: (v: number) => string;
  context?: (kpis: StatisticsKpis) => string;
}[] = [
  {
    key: "total_entities",
    label: "Total Entities",
    icon: Boxes,
    tone: "magenta",
    format: (v) => formatCount(v),
  },
  {
    key: "total_groups",
    label: "Total Groups",
    icon: Network,
    tone: "purple",
    format: (v) => formatCount(v),
  },
  {
    key: "total_messages",
    label: "Total Messages",
    icon: MessagesSquare,
    tone: "cyan",
    format: (v) => formatCount(v),
  },
  {
    key: "active_users_30d",
    label: "Active Users (30d)",
    icon: UserCheck,
    tone: "cyan",
    format: (v) => formatCount(v),
    context: (kpis) =>
      kpis.total_entities > 0
        ? `${Math.round((kpis.active_users_30d / kpis.total_entities) * 100)}% of total`
        : "",
  },
  {
    key: "active_groups_30d",
    label: "Active Groups (30d)",
    icon: Users,
    tone: "purple",
    format: (v) => formatCount(v),
    context: (kpis) =>
      kpis.total_groups > 0
        ? `${Math.round((kpis.active_groups_30d / kpis.total_groups) * 100)}% of all groups`
        : "",
  },
  {
    // IR-1046: groups with no message in the last 30 days
    // (kpis.inactive_groups_30d, backend compute_statistics).
    key: "inactive_groups_30d",
    label: "Inactive Groups (30d)",
    icon: UserMinus,
    tone: "orange",
    format: (v) => formatCount(v),
    context: (kpis) =>
      kpis.total_groups > 0
        ? `${Math.round(((kpis.inactive_groups_30d ?? 0) / kpis.total_groups) * 100)}% of all groups`
        : "",
  },
  {
    key: "avg_messages_per_group",
    label: "Avg Msgs/Group",
    icon: Hash,
    tone: "magenta",
    format: (v) => formatCount(v),
  },
];

export function KpiCards({ kpis, isRefreshing }: KpiCardsProps) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 16 }}>
      {CARDS.map((card) => {
        const Icon = card.icon;
        const tone = TONE[card.tone];
        return (
          <div
            key={card.key}
            className="iris-card"
            style={{
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minHeight: 128,
            }}
          >
            {/* Colored icon chip — IRIS "The Numbers" card design (IR-1046 ④) */}
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "var(--iris-r-md)",
                display: "grid",
                placeItems: "center",
                color: tone.fg,
                background: tone.bg,
                border: `1px solid ${tone.border}`,
              }}
            >
              <Icon size={18} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div className="iris-meta">{card.label}</div>
              <div
                className="iris-kpi"
                style={{ fontSize: 26, lineHeight: 1 }}
                // IR-1046 ⑤: compact value (894K) shown; exact on hover.
                title={(kpis[card.key] ?? 0).toLocaleString()}
              >
                {/* `?? 0` guards a KPI the backend snapshot may not yet carry
                    (FE + backend deploy independently — e.g. inactive_groups_30d
                    before its prod cherry-pick lands). */}
                {card.format(kpis[card.key] ?? 0)}
              </div>
              {isRefreshing ? (
                <p className="iris-meta" style={{ color: "var(--iris-neon-purple)" }}>
                  Updating...
                </p>
              ) : (
                card.context && <p className="iris-meta">{card.context(kpis)}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
